import { Modal as AntModal } from "antd";
import { ModalProps as AntModalProps } from "antd/lib/modal";
import * as React from "react";
import styled, { css } from "styled-components";

import { Button, ButtonGroup, TextButton } from "./Button";
import { StatefulButton } from "./StatefulButton";
import { isMobile, mediaMax } from "./common";
import { theme } from "./theme";
import Icon from "./Icon";
import { isUndefined, omit } from "lodash";

export interface ModalProps extends AntModalProps {
  title?: string | JSX.Element;
  content?: string | JSX.Element;
  okText?: string;
  onOk?: () => void;
  cancelText?: string;
  onCancel?: (event: React.MouseEvent) => void;
  onClose?: (event?: React.MouseEvent) => void;
  closable?: boolean;
  centered?: boolean;
  isOkButtonLoading?: boolean;
  isCancelButtonLoading?: boolean;
  isNotPrimaryBTN?: boolean;
  disableOKButton?: boolean;
  isButtonLoading?: boolean;
  isDataLoading?: boolean;
  isBottomModal?: boolean;
  className?: string;
  isFullscreen?: boolean;
  isBottomCancelButtonVisible?: boolean;
  footerExtraContent?: React.ReactNode;
  customTitle?: React.ReactNode;
  customContent?: React.ReactNode;
  buttonMinWidth?: number;
}

const Modal: React.FC<ModalProps> = (props) => {
  const {
    visible,
    title,
    content,
    width,
    okText,
    cancelText,
    onOk,
    onCancel,
    onClose,
    closable = true,
    isNotPrimaryBTN,
    disableOKButton,
    isButtonLoading,
    isDataLoading,
    isBottomModal,
    className,
    isBottomCancelButtonVisible = true,
    footerExtraContent,
    customTitle,
    customContent,
    buttonMinWidth,
    isFullscreen: isFullScreenDefault,
  } = props;

  const isFullscreen =
    isUndefined(isFullScreenDefault) && isMobile(screen.width)
      ? true
      : isFullScreenDefault;

  const selectedProps = omit(props, [
    "title",
    "customTitle",
    "content",
    "customContent",
    "onOk",
    "okText",
    "onCancel",
    "cancelText",
    "closable",
    "onClose",
    "isOkButtonLoading",
    "isButtonLoading",
    "isDataLoading",
    "isBottomModal",
    "isNotPrimaryBTN",
    "disableOKButton",
    "buttonMinWidth",
    "isFullscreen",
  ]);

  return (
    <ModalWrapper
      centered
      width={width || ""}
      {...selectedProps}
      closable={false}
      visible={visible}
      footer={null}
      onCancel={onClose || onCancel}
      isBottomModal={isBottomModal}
      isFullscreen={isFullscreen}
      className={className}
    >
      {closable && (onClose || onCancel) && (
        <CloseBtn
          className="modal-close"
          data-test="close_button"
          data-testid={"close-button"}
          onClick={onClose || onCancel}
          disabled={isButtonLoading}
        >
          <Icon type="cross" />
        </CloseBtn>
      )}
      {customTitle
        ? customTitle
        : title && <Title className="modal-title">{title}</Title>}
      {customContent ? customContent : content && <Content>{content}</Content>}
      <ChildWrapper
        className="modal-content-children"
        isFullscreen={isFullscreen}
      >
        {props.children}
      </ChildWrapper>
      {(onCancel || onOk) && (
        <GroupButtons isFullscreen={isFullscreen} minWidth={buttonMinWidth}>
          {footerExtraContent}
          {onCancel && isBottomCancelButtonVisible && (
            <StatefulButton
              onClick={onCancel}
              disabled={isButtonLoading}
              className="modal-cancel-btn"
              data-test="cancel_button"
            >
              {cancelText || "Cancel"}
            </StatefulButton>
          )}
          {onOk && (
            <StatefulButton
              buttontype={isNotPrimaryBTN ? undefined : "primary"}
              loading={isButtonLoading}
              disabled={!visible || isDataLoading || disableOKButton}
              onClick={isDataLoading ? undefined : onOk}
              className="modal-confirm-btn"
              data-test="confirm_button"
            >
              {okText || "OK"}
            </StatefulButton>
          )}
        </GroupButtons>
      )}
    </ModalWrapper>
  );
};

const ModalWrapper = styled(AntModal)<{
  isBottomModal?: boolean;
  isFullscreen?: boolean;
}>`
  padding: 0;
  margin: 24px;

  .ant-modal-content {
    padding: 32px;
    ${(props) =>
      !props.width &&
      `
      width: 600px;
    `}
    border-radius: 3px;
    ${mediaMax.sm`
        padding: 24px 16px;
        height: 100%;
        width: 100%;
        min-width: calc(100vw - 48px);
        border-radius: 0;
      `}
  }
  .ant-modal-body {
    position: relative;
    padding: 0;
  }

  ${({ isFullscreen, isBottomModal }) => css`
    ${isFullscreen && fullScreenModalWrapperStyles}
    ${isBottomModal && bottomModalWrapperStyles};
  `}
`;

const fullScreenModalWrapperStyles = `
   && {
        top: 0;
        margin: 0;
        padding: 0;
        height: 100%;
        width: 100%;
        max-width: 100%;
      }
      
    .ant-modal-content {
        padding: 24px 16px;
        height: 100%;
        width: 100%;
        border-radius: 0;
    }
    
    .ant-modal-body {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
`;

const bottomModalWrapperStyles = `
  && {
        vertical-align: bottom;
        height: auto;
      }
`;

const ChildWrapper = styled.div<{ isFullscreen?: boolean }>`
  ${(props) =>
    props.isFullscreen &&
    css`
      flex: 1 1 0%;
      margin: 0 -16px;
      padding: 0 16px;
      overflow-y: auto;
    `}
`;

export const Title = styled.p`
  font-size: ${theme?.fontSize?.title2};
  font-weight: 700;
  line-height: 2.6rem;
  color: ${theme?.textColor};
  padding-right: 20px;
  margin-bottom: 16px;
  ${mediaMax.sm`
    margin-bottom: 24px;
  `};
`;
const Content = styled.div`
  font-size: ${theme?.fontSize?.body};
  font-weight: 400;
  line-height: 2.4rem;
  color: ${theme?.textColor};
  margin-bottom: 1em;
  :last-child {
    margin: 0;
  }
`;

export const GroupButtons = styled(ButtonGroup)<{
  isFullscreen?: boolean;
  minWidth?: number;
}>`
  display: flex;
  justify-content: end;
  flex-wrap: wrap;
  padding-top: 15px;
  margin-top: 24px;
  ${mediaMax.sm`
     gap: 15px 0px;
     flex-wrap: wrap-reverse;
  `};

  @media (max-width: 372px) {
    gap: 2px;
    flex-direction: column-reverse;
    align-items: center;
  }

  ${/* sc-selector */ Button} {
    margin: 0 4px;
    font-weight: 600;
    font-size: ${(props) => props?.theme?.fontSize?.body};
    min-width: ${(props) => (props.minWidth ? props.minWidth : 84)}px;

    ${mediaMax.sm`
      flex: 1 1 auto;
      min-width: 140px;
    `};

    @media (max-width: 372px) {
      width: 95%;
      margin: 6px auto !important;

      &:nth-child(1) {
        margin-bottom: 0 !important;
      }
    }
  }

  ${(props) => props.isFullscreen && fullscreenGroupButtonsStyles}
`;

const fullscreenGroupButtonsStyles = `
      padding-top: 0;
      margin-left: 0;
      margin-right: 0;
      ${Button} {
        flex: auto;
      }
`;

export const CloseBtn = styled(TextButton)`
  position: absolute;
  display: block;
  text-overflow: initial;
  white-space: initial;
  width: 20px;
  height: 20px;
  line-height: 20px;
  top: 0;
  right: 0;
  ${Icon} {
    font-size: ${theme?.fontSize?.title2};
    font-weight: 700;
    margin: 0;
  }
`;

export { Modal };

export default Modal;
